import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../../components/Button";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { Textarea } from "../../../../../components/Textarea";
import { TaskPriority } from "../../../../../model/task/types/TaskPriority.enum";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { Radio } from "../../../../../utils/local-antd";
import { getTaskPriorityColor } from "../../../../../utils/taskUtils";

const PriorityOption = styled(Radio.Button)<{ $priority: TaskPriority }>`
  border: ${({ theme, $priority }) =>
    `1px solid ${theme[getTaskPriorityColor($priority)]}`} !important;
  color: ${({ theme, $priority }) =>
    theme[getTaskPriorityColor($priority)]} !important;

  &.ant-radio-button-wrapper-checked {
    background-color: ${({ theme, $priority }) =>
      theme[getTaskPriorityColor($priority)]} !important;
    color: ${({ theme }) => theme.white} !important;
  }

  &.ant-radio-button-wrapper::before {
    display: none;
  }
`;

type Props = {
  initialBody?: string;
  initialPriority?: TaskPriority;
  submitTitle: string;
  loading?: boolean;
  /** Resolves when the comment was saved, so the form knows to clear itself */
  onSubmit: (body: string, priority: TaskPriority) => Promise<boolean>;
  onCancel?: () => void;
  autoFocus?: boolean;
};

export const WACommentForm: React.FC<Props> = ({
  initialBody = "",
  initialPriority = TaskPriority.LOW,
  submitTitle,
  loading,
  onSubmit,
  onCancel,
  autoFocus,
}) => {
  const { t } = useTranslation();

  const [body, setBody] = useState(initialBody);
  const [priority, setPriority] = useState(initialPriority);

  const trimmed = body.trim();

  const submit = async () => {
    if (!trimmed || loading) {
      return;
    }

    const saved = await onSubmit(trimmed, priority);

    // Editing unmounts the form on success; a new comment starts over
    if (saved && !initialBody) {
      setBody("");
      setPriority(TaskPriority.LOW);
    }
  };

  useEffect(() => {
    setBody(initialBody);
    setPriority(initialPriority);
  }, [initialBody, initialPriority]);

  return (
    <Flex flexDirection="column" gap="s" width="100%">
      <Textarea
        value={body}
        placeholder={t("integration.whatsapp.meta.comments.placeholder")}
        maxLength={Lengths.LONG}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          // The same shortcut the composer's textareas elsewhere take
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        autoFocus={autoFocus}
        rows={3}
        required
        dir="auto"
      />

      <Flex alignItems="center" gap="s" flexWrap="wrap">
        <Text fontSize="s">{t("common.priority")}</Text>

        <Radio.Group
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          size="small"
        >
          {Object.values(TaskPriority).map((level) => (
            <PriorityOption key={level} value={level} $priority={level}>
              {t(`common.${level.toLowerCase()}`)}
            </PriorityOption>
          ))}
        </Radio.Group>

        <Flex gap="s" marginInlineStart="auto">
          {onCancel ? (
            <Button
              onClick={onCancel}
              backgroundColor="transparent"
              color="darkerSub"
              border="none"
            >
              {t("common.cancel")}
            </Button>
          ) : null}

          <Button
            onClick={submit}
            loading={loading}
            disabled={!trimmed || loading}
          >
            {submitTitle}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
